import { useEffect, useState } from 'react';
import { getSiteContent } from '../services/storage';
import type { Language } from '../types/lang.types';

/** Ein Text in allen drei Sprachen */
export type LText = { de: string; en: string; ar: string };

/** Liefert den Text in der aktuellen Sprache (Fallback: Deutsch) */
export function tr(text: LText | string | undefined, lang: Language | string): string {
  if (text == null) return '';
  if (typeof text === 'string') return text;
  return text[lang as Language] || text.de || '';
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Legt gespeicherte Inhalte über die Standardinhalte.
 * Objekte werden rekursiv zusammengeführt, Arrays und Werte komplett ersetzt.
 */
export function mergeContent<T>(defaults: T, saved: unknown): T {
  if (saved === undefined || saved === null) return defaults;
  if (isPlainObject(defaults) && isPlainObject(saved)) {
    const out: Record<string, unknown> = { ...defaults };
    Object.keys(saved).forEach((k) => {
      out[k] = k in defaults ? mergeContent((defaults as Record<string, unknown>)[k], saved[k]) : saved[k];
    });
    return out as T;
  }
  if (isPlainObject(defaults)) return defaults;
  if (Array.isArray(defaults)) return (Array.isArray(saved) ? saved : defaults) as T;
  if (typeof defaults === 'string' && typeof saved !== 'string') return defaults;
  return saved as T;
}

// Zwischenspeicher, damit Layout und Seiten denselben Inhalt nur einmal laden
const cache = new Map<string, Promise<unknown>>();

export function invalidateSiteContent(key?: string): void {
  if (key) cache.delete(key);
  else cache.clear();
}

function load(key: string): Promise<unknown> {
  if (!cache.has(key)) {
    cache.set(key, getSiteContent(key).catch(() => null));
  }
  return cache.get(key)!;
}

/** Lädt den Inhalt einer Seite aus der Datenbank und ergänzt fehlende Felder mit den Standardwerten */
export function useSiteContent<T>(key: string, defaults: T): T {
  const [content, setContent] = useState<T>(defaults);

  useEffect(() => {
    let active = true;
    load(key).then((saved) => {
      if (active) setContent(mergeContent(defaults, saved));
    });
    return () => { active = false; };
    // defaults sind statische Modul-Konstanten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return content;
}
